import type { LoaderFunctionArgs } from "react-router";

import {
  type ClientDayPlanOverride,
  getDayPlanOverride,
} from "~/features/day-plan/cookie.server";
import {
  dayPlanPlaceholder,
  getCurrentDayPlans,
  isPlaceholder,
} from "~/features/day-plan/utils";
import { AlertError } from "~/features/error-handling/AlertError";
import { type AcknowledgeDayPlanResponse } from "~/features/integration/dayplan-distribution/client.server";
import { hrInfoEmployeeClient } from "~/features/integration/hr-api/employee-client.server";
import { populateStaffWithEmployeeAndType } from "~/features/integration/hr-api/populate-staff-employee";
import { dayPlanFetchedSNSClient } from "~/features/integration/sns/dayplan-fetched-client.server";
import { topasAggregatesClient } from "~/features/integration/topas-aggregates/client.server";
import { commonLogger } from "~/features/logging/logging.server";
import { dayPlanQuery } from "~/features/query-cache/queries";
import type { QueryMeta } from "~/features/query-cache/utils";
import { useRootData } from "~/hooks/use-root-data";
import { TimeoutError } from "~/utils/fetch";
import { fetchJson, postJson } from "~/utils/fetch-json";
import { badRequest } from "~/utils/response.server";
import { resolveEmployeeNumber } from "~/utils/user";
import { type AcknowledgeDayPlanVariables } from "./api.acknowledge-dayplan";

import { getErrorMessage } from "@azure/core-util";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Employee } from "@vy-clients/hr-info-api";
import { DayPlanTrackingStatus } from "@vy-clients/topas-aggregates";
import { addDays } from "date-fns";
import type { Jsonify } from "type-fest";

export async function loader({ context, request }: LoaderFunctionArgs) {
  const client = topasAggregatesClient(context);
  const user = context.session?.user;
  const hrInfoClient = hrInfoEmployeeClient(user);
  const dayplanFetchedClient = dayPlanFetchedSNSClient(context.session);

  const dayplanOverride = await getDayPlanOverride(request, context);

  const employeeNumber = dayplanOverride?.employeeNumber ?? user?.employeeId;

  if (!employeeNumber) {
    throw badRequest("Missing employee number");
  }

  const baseDate = dayplanOverride?.date ?? new Date();

  const dates: Date[] = [
    addDays(baseDate, -2),
    addDays(baseDate, -1),
    baseDate,
    addDays(baseDate, 1),
    addDays(baseDate, 2),
  ];

  const results = await Promise.all(
    dates.map(async (date) => {
      const result = await client.getDayPlan({
        date,
        employeeNumber,
      });

      // Create a placeholder instead of returning null, so we know what date each "missing" day plan represents.
      return result ?? dayPlanPlaceholder(date, null);
    }),
  );

  const currentDayPlans = getCurrentDayPlans(results, baseDate);

  const employeeNumbers = new Set<string>();
  for (const dayPlan of Object.values(currentDayPlans)) {
    if (isPlaceholder(dayPlan)) {
      continue;
    }

    const staff = dayPlan.dutyAggregates
      .flatMap((it) => it.taskAggregates)
      .flatMap((it) => it.staffOnboardAggregate);

    for (const employee of staff) {
      employeeNumbers.add(employee.employeeNumber);
    }

    if (user?.employeeId && user.employeeId !== dayPlan.employeeId) {
      commonLogger.debug(
        "Not sending ack message, as employeeId has been overridden",
      );
    } else {
      void dayplanFetchedClient
        .publishDayPlanFetchedMessage({
          employeeId: dayPlan.employeeId,
          dayPlanDate: dayPlan.date.toISOString().slice(0, 10),
          lastUpdatedTimestamp: dayPlan.lastUpdatedAt.toISOString(),
          fetchedTimestamp: new Date().toISOString(),
        })
        .catch((error) => {
          commonLogger.error("day_plan_ack_failed", {
            error: getErrorMessage(error),
          });
        });
    }
  }

  const employees = await hrInfoClient.getEmployees([...employeeNumbers]);

  const employeesByEmployeeNumber: Record<string, Employee> = employees.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.employeeNumber]: curr,
    }),
    {},
  );

  return {
    prev: populateStaffWithEmployeeAndType(
      currentDayPlans.prev,
      employeesByEmployeeNumber,
    ),
    curr: populateStaffWithEmployeeAndType(
      currentDayPlans.curr,
      employeesByEmployeeNumber,
    ),
    next: populateStaffWithEmployeeAndType(
      currentDayPlans.next,
      employeesByEmployeeNumber,
    ),
  };
}

export type ClientDayPlanResponse = Jsonify<Awaited<ReturnType<typeof loader>>>;

export type ClientDayPlansKey = keyof ClientDayPlanResponse;

export type ClientDayPlan = ClientDayPlanResponse[ClientDayPlansKey];

export type ClientDayPlansQuery = ReturnType<typeof useDayPlans>;

export const dayPlanQueryFn = () =>
  fetchJson<ClientDayPlanResponse>("/api/day-plan", {
    timeoutMs: 15_000,
  });

export const useDayPlans = (override?: ClientDayPlanOverride | null) => {
  const rootData = useRootData();

  const resolvedEmployeeNumber = resolveEmployeeNumber(
    rootData?.user?.employeeId,
    override,
  );

  return useQuery({
    enabled: !!resolvedEmployeeNumber,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    retry(count, error) {
      if (error instanceof TimeoutError) {
        return false;
      }
      if (error instanceof AlertError) {
        return false;
      }

      return count < 3;
    },
    queryFn: dayPlanQueryFn,
    ...dayPlanQuery(resolvedEmployeeNumber, override?.date),
    meta: {
      toasts: {
        error: "Henting av dagsverk feilet.",
      },
    } satisfies QueryMeta,
  });
};

export const useAcknowledgeDayPlan = (dataKey: ClientDayPlansKey) => {
  const rootData = useRootData();
  const employeeNumber = rootData?.user?.employeeId;
  const override = rootData?.dayPlanOverride;
  const { queryKey: dayPlanQueryKey } = dayPlanQuery(
    resolveEmployeeNumber(employeeNumber, override),
    override?.date,
  );
  const mutationFn = (body: AcknowledgeDayPlanVariables) =>
    postJson<AcknowledgeDayPlanResponse>("/api/acknowledge-dayplan", body, {
      timeoutMs: 15_000,
    });

  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["day-plans", "acknowledge"],
    mutationFn,
    onMutate: async (_) => {
      const previousData =
        queryClient.getQueryData<ClientDayPlanResponse>(dayPlanQueryKey);

      await queryClient.cancelQueries({ queryKey: dayPlanQueryKey });

      // new Date() should be sufficient for optimistic update purposes.
      queryClient.setQueryData(
        dayPlanQueryKey,
        updateDayPlanResponse(dataKey, new Date().toISOString()),
      );

      // returning previous for context
      return { previousData };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(dayPlanQueryKey, context?.previousData);
    },
    onSuccess: async (result) => {
      if (result.status === "ok") {
        queryClient.setQueryData(
          dayPlanQueryKey,
          updateDayPlanResponse(dataKey, result.data?.acknowledgedAt),
        );
      } else {
        await queryClient.invalidateQueries({
          queryKey: dayPlanQueryKey,
        });
      }
    },
  });
};

function updateDayPlanResponse(
  dataKey: ClientDayPlansKey,
  ackTime?: string | Date | null,
) {
  const normalizedAckTime =
    ackTime instanceof Date ? ackTime.toISOString() : ackTime;
  return (prevData?: ClientDayPlanResponse | null) =>
    prevData &&
    normalizedAckTime && {
      ...prevData,
      [dataKey]: {
        ...prevData[dataKey],
        acknowledgedAt: normalizedAckTime,
        status: DayPlanTrackingStatus.READ,
      },
    };
}
